import MemberRelation, { MemberRelationMember } from '../dto/MemberRelation';
import Department from '../dto/Department';
import DepartmentMemberRelation from '../dto/DepartmentMemberRelation';
import DogMemberRelation from '../dto/DogMemberRelation';
import Error from '../model/Error';
import ExamResult from '../dto/club/ExamResult.js';
import Helper from '@legacy/js/accounting/Helper.js';
import Login from '../dto/Login';
import Member from '../dto/Member';
import MemberClassification from '../dto/MemberClassification';
import MemberGuestContract from '../dto/MemberGuestContract';
import MemberHonor from '../dto/MemberHonor';
import MemberOfferingBooking from '../dto/MemberOfferingBooking';
import MemberPassiveContract from '../dto/MemberPassiveContract';
import MemberRegularContract from '../dto/MemberRegularContract';
import MemberRole from '../dto/MemberRole';
import MemberShortProfile from '../dto/MemberShortProfile';
import MemberShortProfileBasic from '../dto/MemberShortProfileBasic';
import Stocktaking from '../dto/Stocktaking';
import StocktakingUnconsideredMember from '../dto/StocktakingUnconsideredMember';
import TeamMemberRelation from '../dto/TeamMemberRelation';
import axios from 'axios';

export default class MemberRepository {

    static findAll(clubId, status, type, additionalQueryParams, search, sort, limit, offset) {
        return new Promise((resolve, reject) => {
            let params = {
                status,
                type,
                sort,
                limit,
                offset
            };
            if (search !== null) {
                params.search = search;
            }
            axios
                .get(`/club/clubs/${clubId}/members?include=academicTitle${additionalQueryParams}`, { params })
                .then(response => resolve(response.data.data.map(data => Member.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findByDepartmentAndTeamIds(clubId, status, type, additionalQueryParams, search, sort, limit, departmentIds, teamIds, offset) {
        return new Promise((resolve, reject) => {
            let params = {
                status,
                type,
                sort,
                limit,
                offset
            };
            params.departmentIds = departmentIds;
            params.teamIds = teamIds;
            if (search !== null) {
                params.search = search;
            }
            axios
                .get(`/club/clubs/${clubId}/members?include=academicTitle${additionalQueryParams}`, { params })
                .then(response => resolve(response.data.data.map(data => Member.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static create(member, clubId) {
        const data = {
            firstName: member.firstName,
            lastName: member.lastName,
            gender: member.gender,
            dateOfBirth: member.dateOfBirth,
        };

        return new Promise((resolve, reject) => {
            axios
                .post(`/club/clubs/${clubId}/members`, data)
                .then(response => resolve(response.headers['location'].split('/').pop()))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findMemberInfosByAccessMediumIdentifier(clubId, accessMediumIdentifier) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/clubs/${clubId}/access-mediums/${accessMediumIdentifier}`)
                .then(data => resolve(data.data.data))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateProfile(member) {
        const data = {
            firstName: member.firstName,
            lastName: member.lastName,
            gender: member.gender,
            dateOfBirth: member.dateOfBirth,
            placeOfBirth: member.placeOfBirth,
            academicTitleId: member.academicTitle ? member.academicTitle.id : null,
            birthName: member.birthName,
            nationality: member.nationality,
            comment: member.comment,
            bookkeepingComment: member.bookkeepingComment,
            company: member.company,
            phoneBusiness: member.phoneBusiness,
            phoneMobileBusiness: member.phoneMobileBusiness,
            emergencyContact: member.emergencyContact,
            emailBusiness: member.emailBusiness
        };

        return new Promise((resolve, reject) => {
            axios
                .put(`/club/members/${member.id}/profile`, data)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }


    static getLoginData(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/login`)
                .then(response => resolve(Login.fromData(response.data.data)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateLoginData(memberId, login) {
        return new Promise((resolve, reject) => {
            axios
                .put(`/club/members/${memberId}/login`, login)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeLoginData(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/members/${memberId}/login`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateContact(member) {
        const data = {
            street: member.street,
            careOf: member.careOf,
            zip: member.zip,
            city: member.city,
            country: member.country,
            phone: member.phone,
            phoneMobile: member.phoneMobile,
            email: member.email,
            company: member.company,
            phoneBusiness: member.phoneBusiness,
            phoneMobileBusiness: member.phoneMobileBusiness,
            emergencyContact: member.emergencyContact,
            emailBusiness: member.emailBusiness
        };

        return new Promise((resolve, reject) => {
            axios
                .put(`/club/members/${member.id}/contact`, data)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static getContracts(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/contracts`)
                .then(response => resolve(response.data.data.map(data => {
                    if (data.type === 'guest') {
                        return MemberGuestContract.fromData(data);
                    } else if (data.type === 'passive') {
                        return MemberPassiveContract.fromData(data)
                    } else if (data.type === 'regular') {
                        return MemberRegularContract.fromData(data)
                    }
                    console.error('contract could not be mapped')
                })))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static getOfferingBookings(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/offering-bookings`)
                .then(response => resolve(response.data.data.map(data => MemberOfferingBooking.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static getMemberClassificationsData(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/attributes/classifications`)
                .then(response => resolve(response.data.data.map(data => MemberClassification.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static getMemberRolesData(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/attributes/roles`)
                .then(response => resolve(response.data.data.map(data => MemberRole.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static getMemberHonorsData(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/attributes/honors`)
                .then(response => resolve(response.data.data.map(data => MemberHonor.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateContracts(memberId, memberContracts) {
        return new Promise((resolve, reject) => {
            axios
                .put(`/club/members/${memberId}/contracts`, {
                    contracts: memberContracts.map(contract => contract.serialize()),
                })
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static uploadAvatar(member, avatar, progressCallback) {
        const config = {
            onUploadProgress: function(progressEvent) {
                if (progressCallback) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    progressCallback(percentCompleted);
                }
            },
            headers: { 'Content-Type': 'application/octet-stream' }
        };

        return new Promise((resolve, reject) => {
            axios
                .put(`/club/members/${member.id}/avatar`, avatar, config)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeAvatar(member) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/members/${member.id}/avatar`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static deleteRelation(memberId, relationId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/members/${memberId}/relations/${relationId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static addRelation(memberId, relatedMemberId, type) {
        return new Promise((resolve, reject) => {
            axios
                .post(`/club/members/${memberId}/relations`, { 'memberId': relatedMemberId, 'type': type })
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateLegalRepresentatives(memberId, legalRepresentativesIds) {
        return new Promise((resolve, reject) => {
            axios
                .put(`/club/members/${memberId}/legal-representatives`, { 'memberIds': legalRepresentativesIds })
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findRelations(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/relations`)
                .then(response => resolve(response.data.data.map(data => MemberRelation.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findLegalRepresentatives(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/legal-representatives`)
                .then(response => resolve(response.data.data.map(data => MemberRelationMember.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findLegalRepresentativesAndRelations(memberId) {
        const promises = [
            MemberRepository.findRelations(memberId),
            MemberRepository.findLegalRepresentatives(memberId),
        ];

        return Promise.all(promises);
    }
    static findStocktaking(clubId, date) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/clubs/${clubId}/members/stocktaking/${date}`)
                .then(response => resolve(response.data.data && response.data.data.length !== 0 ? Stocktaking.fromData(response.data.data) : null))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }
    static findStocktakingUnconsideredMembers(clubId, date, limit, offset) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/clubs/${clubId}/members/stocktaking/${date}/unconsidered`, { params: { offset: offset, limit: limit } })
                .then(response => resolve(response.data.data.map(data => StocktakingUnconsideredMember.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }
    static countStocktakingUnconsideredMembers(clubId, date) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/clubs/${clubId}/members/stocktaking/${date}/unconsidered/count`)
                .then(response => resolve(response.data.data.count))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static async getStocktakingFile(clubId, selectedYear) {
        try {
            const response = await axios.get(`/club/clubs/${clubId}/members/stocktaking/${selectedYear}/file`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const filename = new Helper().getFilenameFromHeaders(response['headers']);
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            throw await Error.fromBlob(error.response);
        }
    }

    static findBadges(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/badges`)
                .then(response => resolve(response.data.data))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createBadgeCounter(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .patch(`/club/members/${memberId}/badges/membership`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static membershipBadgeUrl(memberId) {
        return `/api/club/members/${memberId}/badges/membership`;
    }

    static remove(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/members/${memberId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static find(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}`)
                .then(response => resolve(Member.fromData(response.data.data)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findDepartments(memberId) {
        return Promise.all([
            new Promise((resolve, reject) => {
                axios
                    .get(`/club/members/${memberId}/departments`)
                    .then(response => resolve(response.data.data.map(data => DepartmentMemberRelation.fromData(data))))
                    .catch(error => reject(Error.fromResponse(error.response)));
            }),
            new Promise((resolve, reject) => {
                axios
                    .get(`/club/members/${memberId}/team-departments`)
                    .then(response => resolve(response.data.data.map(data => Department.fromData(data))))
                    .catch(error => reject(Error.fromResponse(error.response)));
            }),
        ]);
    }

    static findTeams(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/teams`)
                .then(response => resolve(response.data.data.map(data => TeamMemberRelation.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createDepartmentRelation(memberId, departmentRelation) {
        const data = {
            start: departmentRelation.start,
            end: departmentRelation.end,
            departmentId: departmentRelation.department.id,
        };

        return new Promise((resolve, reject) => {
            axios
                .post(`/club/members/${memberId}/departments`, data)
                .then(response => resolve(response.headers['location'].split('/').pop()))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createMemberClassification(memberId, memberClassification) {
        return new Promise((resolve, reject) => {
            axios
                .post(`/club/members/${memberId}/attributes/classifications`, memberClassification.serialize())
                .then(response => resolve(response.headers['X-Identifier']))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createMemberRole(memberId, memberRole) {
        return new Promise((resolve, reject) => {
            axios
                .post(`/club/members/${memberId}/attributes/roles`, memberRole.serialize())
                .then(response => resolve(response.headers['X-Identifier']))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createMemberHonor(memberId, memberHonor) {
        return new Promise((resolve, reject) => {
            axios
                .post(`/club/members/${memberId}/attributes/honors`, memberHonor.serialize())
                .then(response => resolve(response.headers['X-Identifier']))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createTeamRelation(memberId, teamRelation) {
        const data = {
            start: teamRelation.start,
            end: teamRelation.end,
            role: teamRelation.role,
            teamId: teamRelation.team.id,
        };

        return new Promise((resolve, reject) => {
            axios
                .post(`/club/members/${memberId}/teams`, data)
                .then(response => resolve(response.headers['location'].split('/').pop()))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateDepartmentRelations(memberId, departmentRelations) {
        return Promise.all(departmentRelations.map(departmentRelation => {
            const data = {
                start: departmentRelation.start,
                end: departmentRelation.end,
                departmentId: departmentRelation.department.id,
            };

            return new Promise((resolve, reject) => {
                axios
                    .put(`/club/members/${memberId}/departments/${departmentRelation.id}`, data)
                    .then(() => resolve())
                    .catch(error => reject(Error.fromResponse(error.response)));
            });
        }));
    }

    static updateTeamRelations(memberId, teamRelations) {
        return Promise.all(teamRelations.map(teamRelation => {
            const data = {
                start: teamRelation.start,
                end: teamRelation.end,
                role: teamRelation.role,
                teamId: teamRelation.team.id,
            };

            return new Promise((resolve, reject) => {
                axios
                    .put(`/club/members/${memberId}/teams/${teamRelation.id}`, data)
                    .then(() => resolve())
                    .catch(error => reject(Error.fromResponse(error.response)));
            });
        }));
    }

    static updateMemberClassifications(memberId, memberClassifications) {
        return Promise.all(memberClassifications.map(memberClassification =>
            new Promise((resolve, reject) => {
                axios
                    .put(`/club/members/${memberId}/attributes/classifications/${memberClassification.id}`, memberClassification.serialize())
                    .then(() => resolve())
                    .catch(error => reject(Error.fromResponse(error.response)));
            })
        ));
    }

    static updateMemberRoles(memberId, memberRoles) {
        return Promise.all(memberRoles.map(memberRole =>
            new Promise((resolve, reject) => {
                axios
                    .put(`/club/members/${memberId}/attributes/roles/${memberRole.id}`, memberRole.serialize())
                    .then(() => resolve())
                    .catch(error => reject(Error.fromResponse(error.response)));
            })
        ));
    }

    static updateMemberHonors(memberId, memberHonors) {
        return Promise.all(memberHonors.map(memberHonor =>
            new Promise((resolve, reject) => {
                axios
                    .put(`/club/members/${memberId}/attributes/honors/${memberHonor.id}`, memberHonor.serialize())
                    .then(() => resolve())
                    .catch(error => reject(Error.fromResponse(error.response)));
            })
        ));
    }

    static removeDepartment(memberId, departmentId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/members/${memberId}/departments/${departmentId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeTeam(memberId, teamId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/members/${memberId}/teams/${teamId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeMemberClassification(memberId, classificationId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/members/${memberId}/attributes/classifications/${classificationId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeMemberRole(memberId, roleId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/members/${memberId}/attributes/roles/${roleId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeMemberHonor(memberId, honorId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/members/${memberId}/attributes/honors/${honorId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static getShortProfile(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/short-profile`)
                .then(response => resolve(MemberShortProfile.fromData(response.data.data)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static getShortProfileBasic(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/short-profile-basic`)
                .then(response => resolve(MemberShortProfileBasic.fromData(response.data.data)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static addDogRelation(clubId, memberId, dogRelation) {
        let data = {
            dogId: dogRelation.dog.id,
            memberId: memberId,
            association: dogRelation.association.value
        };

        return new Promise((resolve, reject) => {
            axios
                .post(`/club/clubs/${clubId}/dogMemberRelations`, data)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeDogRelation(memberId, dogRelationId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/members/${memberId}/dogRelations/${dogRelationId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findDogRelations(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/dogRelations`)
                .then(response => resolve(response.data.data.map(data => DogMemberRelation.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findDogFiles(memberId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/dogFiles`)
                .then(response => resolve(response.data.data.map(data => DogMemberRelation.fromFileData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static avatarUrl(memberId, size, fileName, cacheBreaker) {
        let url = `/api/club/members/${memberId}/avatar/${fileName}`;

        const params = new URLSearchParams();
        if (size) {
            params.append('size', size);
        }
        if (cacheBreaker) {
            params.append('cb', Date.now());
        }

        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }

        return url;
    }

    static triggerDepartmentAttendanceAction(memberId, departmentId) {
        return new Promise((resolve, reject) => {
            axios
                .put(`/club/members/${memberId}/department-attendances`, { departmentId })
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static triggerTeamAttendanceAction(memberId, teamId) {
        return new Promise((resolve, reject) => {
            axios
                .put(`/club/members/${memberId}/team-attendances`, { teamId })
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createMemberExam(memberId, examId, examOfferingId = null, examDate = null) {
        return new Promise((resolve, reject) => {
            axios
                .post(`/club/members/${memberId}/exam-results`, {
                    examId: examId,
                    examDate: examDate,
                    examOfferingId: examOfferingId,
                })
                .then((response) => resolve(response.headers['location'].split('/').at(-2)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findExamResult(memberId, examId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/exam-results/${examId}`)
                .then((response) => resolve(ExamResult.fromData(response.data.data)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateExamResult(memberId, result) {
        return new Promise((resolve, reject) => {
            axios
                .put(`/club/members/${memberId}/exam-results/${result.id}`, result.serialize())
                .then((response) => resolve(ExamResult.fromData(response.data.data)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static deleteExamResult(memberId, result) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/members/${memberId}/exam-results/${result.id}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findAllExamResults(memberId, params) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/members/${memberId}/exam-results`, { params })
                .then((response) => resolve(response.data.data.map((exam) => ExamResult.fromData(exam))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }
}
